import { useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useParams } from '@tanstack/react-router'
import {
  CheckCircle2,
  FileText,
  Loader2,
  PauseCircle,
  UploadCloud,
  Video,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  DECISION_REJECT_KEY,
  decisionReasonOutputKey,
  type RunbookHumanFormDTO,
  type RunbookHumanFormFieldDTO,
} from '@deepguide-ai/types'
import { trpc } from '@/lib/trpc'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { formatFileSize } from '@/utils/format-file-size'
import {
  EVIDENCE_FILE_ACCEPT,
  EVIDENCE_FILE_MAX_SIZE_LABEL,
  getEvidenceFilesTooLargeMessage,
  splitEvidenceFilesBySize,
} from '@/lib/evidence-file-upload'
import { CHROME_WEB_STORE_URL, startBrowserCapture } from '@/lib/extensionUtils'
import { activeOrganizationWithStorageAtom } from '@/stores/workspaceStore'

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export type RunbookHumanActionStepRun = {
  id: string
  stepId: string
  stepKind: string
  status: string
  title?: string | null
  instruction?: string | null
  outputs?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/** The consolidated `human` step exposes its resolved form on outputs.form. */
function getHumanForm(
  stepRun: RunbookHumanActionStepRun
): RunbookHumanFormDTO | null {
  const outputs = isRecord(stepRun.outputs) ? stepRun.outputs : {}
  if (stepRun.stepKind !== 'human' && outputs.kind !== 'human') return null
  const form = outputs.form
  if (!isRecord(form) || !Array.isArray(form.fields)) return null
  return {
    displayData: Array.isArray(form.displayData)
      ? (form.displayData as RunbookHumanFormDTO['displayData'])
      : [],
    fields: form.fields as RunbookHumanFormFieldDTO[],
    decision: isRecord(form.decision)
      ? (form.decision as RunbookHumanFormDTO['decision'])
      : undefined,
  }
}

function getHumanActionCopy(stepRun: RunbookHumanActionStepRun) {
  const outputs = isRecord(stepRun.outputs) ? stepRun.outputs : {}
  return {
    heading: stepRun.title || 'Action required',
    // Prefer the executor-resolved instruction (real values, not raw refs).
    body:
      (typeof outputs.instruction === 'string' && outputs.instruction) ||
      stepRun.instruction ||
      'Complete the form below to continue the run.',
  }
}

interface RunbookHumanActionPanelProps {
  executionId: string
  statusId?: string | null
  stepRun: RunbookHumanActionStepRun
  compact?: boolean
  onCompleted?: () => void
}

export function RunbookHumanActionPanel({
  executionId,
  stepRun,
  compact = false,
  onCompleted,
}: RunbookHumanActionPanelProps) {
  const queryClient = useQueryClient()
  const copy = useMemo(() => getHumanActionCopy(stepRun), [stepRun])
  // The consolidated `human` step renders its form; `values` holds the text
  // field answers keyed by field id (files come from the StepRun, the decision
  // from the chosen button).
  const humanForm = useMemo(() => getHumanForm(stepRun), [stepRun])
  const hasFilesField = !!humanForm?.fields.some((f) => f.type === 'files')
  const hasRecordingField = !!humanForm?.fields.some(
    (f) => f.type === 'browser_recording',
  )
  const decision = humanForm?.decision
  const decisionExecutionQuery = useQuery({
    ...trpc.runbook.decisionExecution.queryOptions({ executionId }),
    enabled: !!decision,
  })
  const policyBasis = decisionExecutionQuery.data?.policyBasis
  const triggerCause = decisionExecutionQuery.data?.triggerCause as
    | { type?: string; eventType?: string }
    | null
    | undefined
  // Whether to surface the policy-guidance card for this decision. Missing
  // guidance is informational — it never blocks the decision (the approver uses
  // their judgment); only a matched basis adds context.
  const showPolicyGuidance =
    !!policyBasis ||
    (triggerCause?.type === 'EVENT' && triggerCause.eventType === 'task.triage')
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const activeOrganization = useAtomValue(activeOrganizationWithStorageAtom)
  const [values, setValues] = useState<Record<string, string>>({})
  // The reason captured alongside a decision (its own bundled field).
  const [reason, setReason] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Run-scoped uploads (RFC-167 §13.14): files attach to the StepRun, not a CES,
  // so upload works even when the run isn't linked to a test. They're promoted
  // to the test's evidence when the run is linked.
  const stepArtifactsQuery = useQuery({
    ...trpc.runbook.listStepArtifacts.queryOptions({ stepRunId: stepRun.id }),
    enabled: hasFilesField,
  })
  const uploadedFiles = stepArtifactsQuery.data ?? []
  const invalidateStepArtifacts = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.runbook.listStepArtifacts.queryKey({
        stepRunId: stepRun.id,
      }),
    })

  const uploadMutation = useMutation(
    trpc.runbook.uploadStepArtifact.mutationOptions({
      onError: (error: { message: string }) => {
        toast.error(`Upload failed: ${error.message}`)
      },
    }),
  )
  const deleteArtifactMutation = useMutation(
    trpc.runbook.deleteStepArtifact.mutationOptions({
      onSuccess: () => invalidateStepArtifacts(),
      onError: (error: { message: string }) => {
        toast.error(`Failed to remove file: ${error.message}`)
      },
    }),
  )

  // ── Browser recording (run-scoped on the StepRun) ───────────────────────
  // A `browser_recording` field launches the extension recorder bound to this
  // StepRun + field; the committed bundle lands on
  // `StepRun.outputs.browserRecordings[]`, which we poll here. Each recording
  // produces several refs (screenshots + summary + signed manifest) sharing a
  // fieldId, so a field is "satisfied" once ≥1 ref carries its id.
  const [pollRecordings, setPollRecordings] = useState(false)
  const recordingsQuery = useQuery({
    ...trpc.runbook.listStepRecordings.queryOptions({ stepRunId: stepRun.id }),
    enabled: hasRecordingField,
    refetchInterval: pollRecordings ? 3000 : false,
  })
  const recordings = useMemo(
    () => (recordingsQuery.data ?? []) as Array<{ fieldId?: string }>,
    [recordingsQuery.data],
  )
  const recordingCountForField = (fieldId: string): number =>
    recordings.filter((r) => r?.fieldId === fieldId).length
  // Stop polling once a recording has landed (any field). The baseline count is
  // captured when polling starts (in launchRecording); when the polled list
  // grows past it we stop and notify — done in an effect so the toast isn't a
  // render-phase side-effect.
  const recordingBaselineRef = useRef<number | null>(null)
  useEffect(() => {
    if (!pollRecordings || recordingBaselineRef.current === null) return
    if (recordings.length > recordingBaselineRef.current) {
      setPollRecordings(false)
      recordingBaselineRef.current = null
      toast.success('Recording captured')
    }
  }, [pollRecordings, recordings.length])

  const launchRecording = async (field: RunbookHumanFormFieldDTO) => {
    if (!workspaceId) {
      toast.error('Missing workspace context')
      return
    }
    const result = await startBrowserCapture({
      organizationId: activeOrganization?.id,
      workspaceId,
      stepRunId: stepRun.id,
      stepFieldId: field.id,
      contextLabel: field.label || 'Browser recording',
      source: 'runbook_human_step',
    })
    if (result.started) {
      recordingBaselineRef.current = recordings.length
      setPollRecordings(true)
      toast.success(
        result.sidepanelOpened
          ? 'Recording started. Follow the extension side panel.'
          : 'Click the DeepGuide extension icon to start recording.',
      )
    } else if (result.reason === 'no-ack') {
      toast.error('DeepGuide extension not detected. Install it to record.')
      window.open(CHROME_WEB_STORE_URL, '_blank')
    } else {
      toast.error(result.error || 'Failed to start recording.')
    }
  }

  // Drop/select → upload each file to the StepRun, then refresh the list.
  const handleFiles = async (selected: File[]) => {
    const { oversizedFiles, validFiles } = splitEvidenceFilesBySize(selected)
    if (oversizedFiles.length) {
      toast.error(getEvidenceFilesTooLargeMessage(oversizedFiles.map((f) => f.name)))
    }
    if (validFiles.length === 0) return

    setUploading(true)
    try {
      for (const file of validFiles) {
        const base64Data = await fileToBase64(file)
        // @ts-expect-error - Known tRPC mutation type inference issue
        await uploadMutation.mutateAsync({
          stepRunId: stepRun.id,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          base64Data,
          size: file.size,
        })
      }
      toast.success(`${validFiles.length === 1 ? 'File' : 'Files'} uploaded`)
      await invalidateStepArtifacts()
    } catch {
      // per-file errors already toasted by the mutation
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const completeMutation = useMutation({
    ...trpc.runbook.completeStepRun.mutationOptions(),
    onSuccess: () => {
      toast.success('Human action submitted')
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getRun.queryKey({ id: executionId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRuns.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.list.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.actionRequiredCount.queryKey(),
      })
      onCompleted?.()
    },
    onError: (error) => {
      toast.error(`Failed to submit action: ${error.message}`)
    },
  })

  // Consolidated `human` form submit. `action` carries the flow decision
  // (approve/reject → the gate; continue otherwise); per-field text answers ride
  // in payload.values keyed by field id (files come from the StepRun bucket),
  // and a decision's reason rides as `<decision.id>_reason`.
  const submitHuman = (
    action: 'continue' | 'approve' | 'reject',
    // The chosen decision outcome key (e.g. `pass`/`fail`), recorded as the
    // step's outcome distinct from the routing `action`. #1459
    decisionKey?: string,
  ) => {
    if (!humanForm) return
    for (const field of humanForm.fields) {
      if (!field.required) continue
      if (field.type === 'files') {
        if (uploadedFiles.length === 0) {
          toast.error(`Attach at least one file for "${field.label || 'Files'}"`)
          return
        }
        continue
      }
      if (field.type === 'browser_recording') {
        if (recordingCountForField(field.id) === 0) {
          toast.error(
            `Record a browser session for "${field.label || 'Recording'}"`,
          )
          return
        }
        continue
      }
      // text / number / date / url all capture a single string value.
      if (!values[field.id]?.trim()) {
        toast.error(`"${field.label || 'A field'}" is required`)
        return
      }
    }
    if (decision?.reasonRequired && !reason.trim()) {
      toast.error('Enter a reason for your decision')
      return
    }
    const submittedValues: Record<string, string> = {}
    for (const field of humanForm.fields) {
      // Every non-files field rides as a single trimmed string value.
      if (field.type !== 'files' && values[field.id]?.trim()) {
        submittedValues[field.id] = values[field.id].trim()
      }
    }
    if (decision && reason.trim()) {
      submittedValues[decisionReasonOutputKey(decision.id)] = reason.trim()
    }
    // Record the chosen outcome key under the decision id (server prefers this
    // over the routing action), so Pass/Fail is captured honestly.
    if (decision && decisionKey) {
      submittedValues[decision.id] = decisionKey
    }
    completeMutation.mutate({
      stepRunId: stepRun.id,
      action,
      payload: {
        values: submittedValues,
        // Mirror the reason as the run-failure message on a reject.
        ...(decision && reason.trim() ? { reason: reason.trim() } : {}),
      },
    })
  }

  // Uploaded-file list + drop-zone, shared by the legacy `upload` step and a
  // `human` form's `files` field.
  const fileUploadBlock = (
    <>
      {uploadedFiles.length > 0 && (
        <ul className='space-y-1.5'>
          {uploadedFiles.map((file) => (
            <li
              key={file.id}
              className='flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 text-[12px]'
            >
              <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
              <span className='min-w-0 flex-1 truncate font-medium text-text-secondary'>
                {file.filename}
              </span>
              <span className='shrink-0 text-[11px] text-text-subtle'>
                {formatFileSize(file.size, '')}
              </span>
              <button
                type='button'
                aria-label={`Remove ${file.filename}`}
                disabled={uploading || deleteArtifactMutation.isPending}
                onClick={() =>
                  deleteArtifactMutation.mutate({
                    stepRunId: stepRun.id,
                    artifactId: file.id,
                  })
                }
                className='shrink-0 text-text-placeholder hover:text-status-red disabled:opacity-50'
              >
                <X className='h-3.5 w-3.5' />
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        type='file'
        ref={fileInputRef}
        multiple
        accept={EVIDENCE_FILE_ACCEPT}
        className='hidden'
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) {
            void handleFiles(Array.from(event.target.files))
          }
        }}
      />
      <div
        role='button'
        tabIndex={0}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!uploading) fileInputRef.current?.click()
          }
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!uploading) setIsDragging(true)
        }}
        onDragLeave={(event) => {
          event.preventDefault()
          setIsDragging(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragging(false)
          if (uploading) return
          if (event.dataTransfer.files.length > 0) {
            void handleFiles(Array.from(event.dataTransfer.files))
          }
        }}
        className={`cursor-pointer rounded-sm border-2 border-dashed px-4 py-6 text-center transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border-divider bg-card hover:border-primary hover:bg-primary/5'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        {uploading ? (
          <Loader2 className='mx-auto mb-2 h-5 w-5 animate-spin text-text-subtle' />
        ) : (
          <UploadCloud className='mx-auto mb-2 h-5 w-5 text-text-subtle' />
        )}
        <div className='text-[12px] font-medium text-text-secondary'>
          {uploading
            ? 'Uploading…'
            : uploadedFiles.length > 0
              ? 'Drag & drop or click to add more files'
              : 'Drag & drop files here, or click to browse'}
        </div>
        <div className='mt-1 text-[11px] text-text-subtle'>
          PDF, Word, Excel, CSV, images up to {EVIDENCE_FILE_MAX_SIZE_LABEL}
        </div>
      </div>
    </>
  )

  // ── Consolidated `human` step: render its form ──────────────────────────
  if (humanForm) {
    return (
      <div className='mx-auto w-full max-w-[640px] space-y-3'>
        {/* Read-only context from earlier steps sits above the action form. */}
        {humanForm.displayData.length > 0 && (
          <dl className='space-y-3'>
            {humanForm.displayData.map((item) => (
              <div key={item.id} className='space-y-1.5 text-[12px]'>
                <dd className='min-w-0 break-words text-[13px] leading-5 text-text-body'>
                  {/^https?:\/\//.test(item.value.trim()) ? (
                    <a
                      href={item.value.trim()}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-cobalt-strong underline hover:text-cobalt-strong'
                    >
                      {item.value.trim()}
                    </a>
                  ) : (
                    item.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}

        <section
          className={`w-full overflow-hidden rounded-md border border-border bg-card shadow-sm ${
            compact ? '' : 'ring-1 ring-status-orange/10'
          }`}
        >
        <div className='flex items-start gap-3 border-b border-border bg-status-orange-bg px-5 py-4'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-orange-bg'>
            <PauseCircle className='h-4 w-4 text-status-orange' />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <div className='text-[14px] font-semibold text-text-secondary'>
                {copy.heading}
              </div>
              <span className='rounded-full bg-status-orange-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-status-orange'>
                Action required
              </span>
            </div>
            <div className='prose prose-sm mt-1.5 max-w-none text-[12px] leading-5 text-text-body [&_a]:text-cobalt-strong [&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5'>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {copy.body}
              </ReactMarkdown>
            </div>
          </div>
        </div>

        <div className='space-y-5 px-5 py-5'>
          {decision &&
            (decisionExecutionQuery.isLoading || showPolicyGuidance) && (
              <div className='space-y-2 rounded-sm border border-border bg-surface-subtle px-4 py-3'>
                <div className='text-[12px] font-semibold text-text-secondary'>
                  Policy guidance for this decision
                </div>
                {decisionExecutionQuery.isLoading ? (
                  <div className='text-text-subtle text-[11px]'>
                    Finding the relevant approved policy guidance…
                  </div>
                ) : policyBasis?.policies.length ? (
                  <>
                    <div className='text-text-subtle text-[11px]'>
                      Policy match: {Math.round(policyBasis.confidence * 100)}%
                      confident · {policyBasis.rationale}
                    </div>
                    <ul className='space-y-2'>
                      {policyBasis.policies.flatMap((policy) =>
                        policy.citations.map((citation) => (
                          <li
                            key={citation.policyClaimId}
                            className='text-text-secondary text-[11px] leading-4'
                          >
                            <span className='font-medium'>
                              {policy.title}
                              {policy.policyDocumentVersion != null
                                ? ` v${policy.policyDocumentVersion}`
                                : ''}
                              :{' '}
                            </span>
                            {citation.claimText}
                          </li>
                        ))
                      )}
                    </ul>
                  </>
                ) : (
                  <div className='text-text-subtle text-[11px]'>
                    No specific approved policy guidance applies to this
                    request. Use your judgment.
                  </div>
                )}
              </div>
            )}

          {/* Input fields — text, files. The decision (if any) renders below. */}
          {humanForm.fields.map((field) => {
            const recordingCount = recordingCountForField(field.id)
            return (
              <div key={field.id} className='space-y-1.5'>
                <Label className='text-[12px] font-semibold text-text-secondary'>
                  {field.label || 'Field'}
                  {field.required && (
                    <span className='ml-1 text-status-red'>*</span>
                  )}
                </Label>
                {field.help && (
                  <p className='text-[11px] text-text-subtle'>{field.help}</p>
                )}
                {field.type === 'files' ? (
                  fileUploadBlock
                ) : field.type === 'browser_recording' ? (
                  <div className='space-y-2'>
                    {recordingCount > 0 && (
                      <div className='flex items-center gap-2 rounded-sm border border-status-green/30 bg-status-green-bg px-3 py-2 text-[12px]'>
                        <CheckCircle2 className='h-4 w-4 shrink-0 text-status-green' />
                        <span className='min-w-0 flex-1 font-medium text-foreground'>
                          Recording captured
                        </span>
                        <span className='shrink-0 text-[11px] text-text-subtle'>
                          {recordingCount} file
                          {recordingCount === 1 ? '' : 's'}
                        </span>
                      </div>
                    )}
                    <Button
                      type='button'
                      size='sm'
                      variant='outline'
                      className='gap-2'
                      disabled={pollRecordings}
                      onClick={() => void launchRecording(field)}
                    >
                      {pollRecordings ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Video className='h-4 w-4' />
                      )}
                      {recordingCount > 0
                        ? 'Re-record'
                        : 'Record browser session'}
                    </Button>
                    <p className='text-[11px] text-text-subtle'>
                      Opens the DeepGuide extension. Screenshots and a signed
                      manifest become this step&apos;s evidence.
                    </p>
                  </div>
                ) : field.type === 'select' ? (
                  <Select
                    value={values[field.id] ?? ''}
                    onValueChange={(next) =>
                      setValues((current) => ({ ...current, [field.id]: next }))
                    }
                  >
                    <SelectTrigger fullWidth className='h-9 bg-card text-[12px]'>
                      <SelectValue placeholder='Select…' />
                    </SelectTrigger>
                    <SelectContent>
                      {(field.options ?? [])
                        // Radix throws on an empty item value — skip any that
                        // slipped past the boundary schema (legacy data).
                        .filter((option) => option.value)
                        .map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            className='text-[12px]'
                          >
                            {option.label || option.value}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                ) : field.type === 'text' ? (
                  <Textarea
                    value={values[field.id] ?? ''}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.id]: event.target.value,
                      }))
                    }
                    placeholder={field.sign ? 'Type your sign-off' : 'Your answer'}
                    className='min-h-[72px] bg-card text-[12px]'
                  />
                ) : (
                  <Input
                    type={
                      field.type === 'number'
                        ? 'number'
                        : field.type === 'date'
                          ? 'date'
                          : 'url'
                    }
                    value={values[field.id] ?? ''}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.id]: event.target.value,
                      }))
                    }
                    placeholder={
                      field.type === 'url' ? 'https://…' : 'Your answer'
                    }
                    className='h-9 bg-card text-[12px]'
                  />
                )}
              </div>
            )
          })}

          {/* Decision gate — a plain reason field, then one button per outcome
              (approve continues, reject takes the failure edge). */}
          {decision && (
            <div className='space-y-1.5'>
              <Label className='text-[12px] font-semibold text-text-secondary'>
                Provide the reason for your decision
                {decision.reasonRequired && (
                  <span className='ml-1 text-status-red'>*</span>
                )}
              </Label>
              {decision.help && (
                <p className='text-[11px] text-text-subtle'>{decision.help}</p>
              )}
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder='Add a brief reason…'
                className='min-h-[64px] bg-card text-[12px]'
              />
            </div>
          )}

          <div className='flex flex-wrap justify-end gap-2 border-t border-border pt-4'>
            {decision ? (
              decision.options.map((option) => (
                <Button
                  key={option.key}
                  size='sm'
                  variant={option.tone === 'negative' ? 'outline' : 'default'}
                  className={
                    option.tone === 'negative'
                      ? 'gap-2 border-status-red/30 text-status-red'
                      : 'gap-2'
                  }
                  disabled={completeMutation.isPending || uploading}
                  onClick={() =>
                    submitHuman(
                      option.tone === 'negative' ||
                        option.key === DECISION_REJECT_KEY
                        ? 'reject'
                        : 'approve',
                      option.key,
                    )
                  }
                >
                  {completeMutation.isPending && option.tone !== 'negative' ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : null}
                  {option.label}
                </Button>
              ))
            ) : (
              <Button
                size='sm'
                className='gap-2'
                disabled={completeMutation.isPending || uploading}
                onClick={() => submitHuman('continue')}
              >
                {completeMutation.isPending ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : null}
                Submit
              </Button>
            )}
          </div>
        </div>
        </section>
      </div>
    )
  }

  return null
}
